// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import WidgetTitle from "@bgs/App/Components/WidgetTitle";
import XNewsItem from "@bgs/App/Widgets/XNewsList/XNewsItem";
import { bgsCarouselStyles, BGSProductCarouselSlider } from "@bgs/XDL/Utilities/sharedStyles";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasShellContext, useShellContext } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const parentPageId = state.links.pageTypesToNodeId["NewsListPage"];

    const parentPageLink = getPageLinkByNodeId(state, parentPageId);
    const pages: any = parentPageId ? getDataView(state.data.pages, { parentNodeId: parentPageId }) : {};
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages && pages.value && pages.value.length > 0) {
        links = pages.value.reduce(
            (links: any, page: { nodeId: string }) => ({
                ...links,
                [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
            }),
            {},
        );
    }
    return {
        parentNodeId: parentPageId || "",
        location: getLocation(state),
        pages,
        links,
        parentPageLink,
        parameter: state.pages.newsList.getNewsPagesParameters[parentPageId] ?? {
            parentNodeId: parentPageId || "",
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    ThemeProps<BaseTheme>;

export interface XNewsListStyles extends BGSProductCarouselSlider {
    sectionWrapper?: InjectableCss;
}

export const newsListViewStyles: XNewsListStyles = {
    ...bgsCarouselStyles,
    sectionWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.ui3")};
        `,
    },
    carouselWrapper: {
        css: css`
            overflow: hidden;
            @media (max-width: 1280px) {
                padding: 20px 0;
                &:before {
                    content: none;
                }
            }
        `,
    },
    innerContainer: {
        css: css`
            display: flex;
            margin-right: -27px;
            @media (max-width: 1024px) {
                margin-right: -20px;
            }
            @media (max-width: 1000px) {
                margin-right: -15;
            }
            @media (max-width: 600px) {
                margin-right: -16px;
            }
        `,
    },
    slide: {
        css: css`
            display: flex;
            justify-content: center;
            min-width: calc(100% / 4 - 27px);
            margin-right: 27px;
            @media (max-width: 1024px) {
                min-width: calc(100% / 3 - 15px);
                margin-right: 15px;
            }
            @media (max-width: 1000px) {
                min-width: calc(100% / 2 - 20px);
                margin-right: 20px;
            }
            @media (max-width: 600px) {
                min-width: calc(100% - 16px);
                margin: 0 8px;
            }
            & {
                height: 476px !important;
            }
            img {
                display: block;
                max-width: 100%;
                max-height: 100%;
                object-fit: fill;
            }
        `,
    },
};

const styles = newsListViewStyles;

const XNewsList: React.FC<Props> = ({
    loadPagesForParent,
    parameter,
    pages,
    parentNodeId,
    links,
    fields,
    theme,
    parentPageLink,
}) => {
    const shellContext = useShellContext();

    React.useEffect(() => {
        if (!pages.value && !pages.isLoading && parentNodeId) {
            loadPagesForParent(parameter);
        }
    }, []);

    React.useEffect(() => {
        if (!pages.value && !pages.isLoading && parentNodeId) {
            loadPagesForParent(parameter);
        }
    }, []);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setSelectedIndex(embla ? embla.selectedScrollSnap() : 0);
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        if (typeof window === "undefined") {
            return 4;
        }
        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, 10000);
    }, [windowResizeTime]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[3];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [pages, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !pages || !pages.value) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: true,
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, pages]);

    if (!pages || !parentNodeId || !pages.value) {
        return null;
    }

    const sortedAndPagedPages = sortBy(pages.value, o => o.fields["publishDate"]).reverse();

    return (
        <StyledWrapper {...styles.sectionWrapper}>
            <WidgetTitle title={fields.title || "Styles & Inspiration"} viewAllLink={parentPageLink?.url} />
            <StyledWrapper {...styles.mainContainer}>
                <StyledWrapper {...styles.navigationWrapper}>
                    {pages && pages.value.length > slidesToScroll && (
                        <Button
                            {...styles.scrollPrev}
                            disabled={!canScrollPrev}
                            onClick={() => embla && embla.scrollPrev()}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                    <Typography weight={fontConfig.weight.semiBold} italic lineHeight="24px">{`${
                        selectedIndex + 1
                    } of ${Math.ceil(pages.value.length / slidesToScroll)}`}</Typography>
                    {pages && pages.value.length > slidesToScroll && (
                        <Button
                            {...styles.scrollPrev}
                            disabled={!canScrollNext}
                            onClick={() => embla && embla.scrollNext()}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.carouselWrapper} ref={emblaRef}>
                    <StyledWrapper data-test-selector="slides" {...styles.innerContainer}>
                        {sortedAndPagedPages.map(page => {
                            const imageObj = page.translatableFields?.openGraphImage;
                            return (
                                <StyledWrapper key={`news_${page.id}`} {...styles.slide}>
                                    <XNewsItem
                                        title={page.fields["title"]}
                                        date={new Date(page.fields["publishDate"])}
                                        description={parse(
                                            page.fields["newsSummary"] || page.fields["newsContent"],
                                            parserOptions,
                                        )}
                                        readMoreLink={links[page.nodeId]?.url}
                                        imgSrc={imageObj[Object.keys(imageObj)[0]]}
                                    />
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(XNewsList)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Blogs",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
